// src/pages/PrivacyPolicy.jsx
import React from 'react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'

const EFFECTIVE = 'April 26, 2026'

export default function PrivacyPolicy() {
  return (
    <div style={s.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Mono:wght@300;400;500&display=swap');
        .hover-gold:hover { color: #c8a96e !important; }
      `}</style>

      <nav style={s.nav}>
        <Link to="/" style={s.navLogo}>
          <div style={s.navIcon}>
            <CrosshairIcon size={18} color="#0a0a0a" />
          </div>
          <div>
            <div style={s.navName}>DISDownrange</div>
            <div style={s.navSub}>by Dynamic Innovative Solutions</div>
          </div>
        </Link>
        <Link to="/" style={s.backLink} className="hover-gold">← Back to Home</Link>
      </nav>

      <main style={s.main}>
        <div style={s.header}>
          <div style={s.eyebrow}>Legal</div>
          <h1 style={s.title}>Privacy Policy</h1>
          <div style={s.meta}>
            Last updated: {EFFECTIVE} · Effective: {EFFECTIVE}
          </div>
        </div>

        <Section title="1. Who We Are">
          <p>
            DISDownrange ("the Service") is operated by Dynamic Innovative Solutions LLC
            ("DIS LLC", "we", "us"), a Missouri limited liability company. The domain
            disdownrange.org is registered to Nicholas Antel personally on behalf of DIS LLC.
            This Privacy Policy describes what information we collect, how we use it, and
            the rights you have over it.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p>We collect only the information needed to operate the Service:</p>
          <ul style={s.ul}>
            <li><b>Account email.</b> Required to create and sign in to your account.</li>
            <li><b>Rifle profiles.</b> Caliber, barrel length, twist rate, sight height, zero
              distance, and any nicknames or notes you save.</li>
            <li><b>Shot logs.</b> If you use Elite-tier shot logging, the rounds, distances,
              corrections, and outcomes you record.</li>
            <li><b>GPS location.</b> Collected only when you tap "Auto-fill from GPS" and
              used solely to retrieve current weather conditions for your shot. Coordinates
              are not stored on our servers.</li>
            <li><b>Payment data.</b> Subscription payments are processed by Stripe. We never
              see or store your card number, CVV, or full bank details. We retain only a
              Stripe customer ID and subscription status.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul style={s.ul}>
            <li>Provide ballistic calculations and account features.</li>
            <li>Sync your rifle profiles and settings across your devices.</li>
            <li>Process subscription payments and manage your tier.</li>
            <li>Diagnose bugs from error reports and improve the Service based on user feedback.</li>
            <li>Send transactional email (account verification, billing receipts, security
              alerts). We do not send marketing email without your opt-in.</li>
          </ul>
        </Section>

        <Section title="4. Third-Party Services">
          <p>The Service relies on the following third parties. Their handling of your data
            is governed by their own privacy policies:</p>
          <ul style={s.ul}>
            <li><b>Supabase</b> — hosts our database and handles authentication. Your
              account email and rifle profiles are stored here.</li>
            <li><b>Stripe</b> — processes payments. Card data is sent directly from your
              browser to Stripe; it does not pass through our servers.</li>
            <li><b>AvantLink</b> — affiliate network used for outbound product links to
              retailers. AvantLink may set tracking cookies when you click an affiliate link,
              attributing any subsequent purchase to DIS LLC for commission purposes.</li>
            <li><b>Open-Meteo</b> — public weather API. Your GPS coordinates are sent to
              Open-Meteo to retrieve forecast data for your shot. Open-Meteo does not
              require an account and does not personally identify users.</li>
          </ul>
        </Section>

        <Section title="5. Cookies and Local Storage">
          <p>We use:</p>
          <ul style={s.ul}>
            <li><b>JWT tokens</b> stored by Supabase in your browser's local storage to keep you signed in.</li>
            <li><b>Local storage</b> in your browser to cache rifle profiles, ammo data, and
              app preferences for offline use.</li>
            <li><b>AvantLink affiliate cookies</b> set when you click an outbound product
              link. These cookies allow attribution of qualifying purchases to DIS LLC and
              do not collect personal information beyond the click event.</li>
          </ul>
          <p>You can clear cookies and local storage at any time through your browser
            settings. Doing so will sign you out and remove cached data.</p>
        </Section>

        <Section title="6. Your Rights (CCPA, GDPR, and Equivalents)">
          <p>Regardless of where you live, you have the right to:</p>
          <ul style={s.ul}>
            <li><b>Access</b> a copy of the personal information we hold about you.</li>
            <li><b>Correct</b> inaccurate information.</li>
            <li><b>Delete</b> your account and associated data.</li>
            <li><b>Port</b> your data in a machine-readable format.</li>
            <li><b>Object</b> to or restrict certain processing.</li>
            <li><b>Opt out</b> of any sale or sharing of personal information for targeted
              advertising. We do not sell your personal information.</li>
          </ul>
        </Section>

        <Section title="7. How to Exercise Your Rights">
          <p>To request access, correction, or deletion of your data, email{' '}
            <a href="mailto:help@disdownrange.org" style={s.inlineLink} className="hover-gold">
              help@disdownrange.org
            </a>{' '}
            from the address associated with your account. We will verify your identity and
            respond within 30 days. There is no charge for routine requests.
          </p>
        </Section>

        <Section title="8. Data Retention">
          <p>Account data and rifle profiles are retained for as long as your account is
            active. If you delete your account, we permanently remove your personal data
            within 30 days, except where we are required by law to retain certain billing
            records (typically up to 7 years for tax purposes). Anonymous, aggregated
            analytics may be retained indefinitely.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>The Service is not directed at children. Because DISDownrange is a tool for
            firearms users, you must be at least 18 years old to use it. We do not knowingly
            collect information from anyone under 18. If you believe a minor has created an
            account, contact us and we will delete the account and any associated data.</p>
        </Section>

        <Section title="10. Security">
          <p>We use industry-standard practices to protect your data: TLS encryption in
            transit, encrypted storage at rest via Supabase, and PCI-compliant payment
            processing via Stripe. No system is perfectly secure; if you suspect your account
            has been compromised, contact us immediately.</p>
        </Section>

        <Section title="11. International Users">
          <p>The Service is operated from the United States. By using DISDownrange you
            consent to the transfer and processing of your data in the United States, where
            data protection laws may differ from those in your country.</p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. Material changes will be
            announced by email and reflected by an updated "Last updated" date at the top of
            this page. Continued use of the Service after changes take effect constitutes
            acceptance of the revised policy.</p>
        </Section>

        <Section title="13. Contact">
          <p>
            Dynamic Innovative Solutions LLC<br/>
            Winona, MO 65588<br/>
            Email:{' '}
            <a href="mailto:help@disdownrange.org" style={s.inlineLink} className="hover-gold">
              help@disdownrange.org
            </a>
          </p>
        </Section>
      </main>

      <Footer />
    </div>
  )
}

function Section({ title, children }) {
  return (
    <section style={s.sect}>
      <h2 style={s.h2}>{title}</h2>
      <div style={s.sectBody}>{children}</div>
    </section>
  )
}

function CrosshairIcon({ size = 22, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="7"/>
      <line x1="12" y1="2" x2="12" y2="5"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
      <line x1="2" y1="12" x2="5" y2="12"/>
      <line x1="19" y1="12" x2="22" y2="12"/>
      <circle cx="12" cy="12" r="1.5" fill={color}/>
    </svg>
  )
}

const s = {
  page: { background: '#0a0a0a', color: '#f0ede8', fontFamily: "'DM Mono', monospace", minHeight: '100vh' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 40px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    position: 'sticky', top: 0, background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(12px)',
    zIndex: 10,
  },
  navLogo: { display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' },
  navIcon: { width: 30, height: 30, background: '#c8a96e', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  navName: { fontFamily: "'Playfair Display', serif", fontSize: 15, fontWeight: 700, color: '#f0ede8' },
  navSub: { fontSize: 9, color: '#c8a96e', letterSpacing: '0.12em', textTransform: 'uppercase' },
  backLink: { fontSize: 12, color: '#888', textDecoration: 'none', fontFamily: "'DM Mono', monospace", transition: 'color 0.2s' },

  main: { maxWidth: 760, margin: '0 auto', padding: '60px 24px 80px' },
  header: { marginBottom: 50, paddingBottom: 30, borderBottom: '1px solid rgba(255,255,255,0.07)' },
  eyebrow: { fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#c8a96e', marginBottom: 14 },
  title: { fontFamily: "'Playfair Display', serif", fontSize: 'clamp(34px, 6vw, 54px)', fontWeight: 900, lineHeight: 1.1, color: '#f0ede8', marginBottom: 18 },
  meta: { fontSize: 11, color: '#666', letterSpacing: '0.04em' },

  sect: { marginBottom: 38 },
  h2: { fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: '#f0ede8', marginBottom: 14, letterSpacing: '0.01em' },
  sectBody: { fontSize: 13, lineHeight: 1.8, color: '#aaa' },
  ul: { marginTop: 10, marginBottom: 10, paddingLeft: 22, display: 'flex', flexDirection: 'column', gap: 8 },
  inlineLink: { color: '#c8a96e', textDecoration: 'none', borderBottom: '1px solid rgba(200,169,110,0.3)', transition: 'color 0.2s' },
}
